import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import useDebounce from "shared/hooks/useDebounce";
import SekeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import {
  DeleteOrganizationService,
  GetOrganizationsListService,
} from "shared/services/organization";
import styles from "./styles.module.scss";
import CustomButton from "shared/components/common/customButton";
import { useNavigate } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { GetCountService } from "shared/services/general";
import { setCountReducer } from "shared/redux/reducers/count";
import { useDispatch } from "react-redux";

const Heads: CustomTableHeadType[] = [
  {
    title: "Join Date",
    key: "created_at",
  },
  {
    title: "Organization",
    key: "name",
  },
  {
    title: "Associated Users",
    key: "users_count",
  },
  {
    title: "License Status",
    key: "status",
  },
  {
    title: "Action",
    key: "",
  },
];

const Organizations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pageRef = useRef<number>(1);

  const [loading, setLoading] = useState<boolean>(true);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchVal, setSearchVal] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const handleGetEvaluations = () => {
    setLoading(true);
    GetOrganizationsListService({ search: searchVal, pageNo: pageRef.current })
      .then(({ data: { data, total } }) => {
        setOrganizations(data);
        setTotal(total);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleGetCount = () => {
    GetCountService()
      .then(({ data }) => {
        dispatch(
          setCountReducer({
            complaints: data?.complaints_count,
            orgs: data?.orgs_count,
            req: data?.req_count,
          })
        );
      })
      .catch((err) => {});
  };

  useEffect(() => {
    handleGetCount();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    pageRef.current = 1;
    setPage(1);
    setOrganizations([]);
    setLoading(true);
    handleGetEvaluations();
    // eslint-disable-next-line
  }, [searchVal]);

  useDebounce(
    () => {
      setSearchVal(search);
    },
    [search],
    800
  );

  return (
    <div className={classNames(styles.mainContainer)}>
      <div
        className={classNames(
          "flex gap-3 items-center justify-end flex-row px-4 py-4",
          styles.tableMainHead
        )}
      >
        <div className={classNames("flex flex-wrap items-center gap-3")}>
          <div className={classNames(styles.searchContainer, "flex")}>
            <Icons.Search className={classNames(styles.searchIconStyle)} />
            <input
              className={classNames(styles.searchInputStyle, "ps-2")}
              value={search}
              placeholder="Search Organizations"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
          <CustomButton
            title="Add New Organization"
            Icon={Icons.PlusCircle}
            IconDirction="left"
            containerStyle={classNames(styles.buttonMain)}
            iconStyle={classNames(styles.buttonIcon)}
            onClick={() => {
              navigate(routeConstant.addOrganization.path);
            }}
          />
        </div>
      </div>
      <CustomTable
        title="Organizations List"
        heads={Heads}
        loading={loading}
        isEmpty={organizations.length === 0}
        stickSecondCol={false}
        evaluations={organizations}
        setEvaluations={setOrganizations}
        stickFirstCol={false}
        maxWidth="700px"
      >
        {loading ? (
          <>
            {Array.from(Array(5).keys()).map((_, index) => {
              return <TableRowLoader key={index} />;
            })}
          </>
        ) : (
          <>
            {organizations.map((organization, index) => {
              return (
                <TableRow
                  item={organization}
                  key={organization?.id}
                  index={index}
                  organizations={organizations}
                  setOrganizations={setOrganizations}
                />
              );
            })}
          </>
        )}
      </CustomTable>

      <div
        className={classNames(
          "w-full px-4 py-4",
          total >= 10 && styles.border,
          styles.paginationContainer
        )}
      >
        <CustomPagination
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(page: any) => {
            pageRef.current = page;
            setPage(page);
            handleGetEvaluations();
          }}
        />
      </div>
    </div>
  );
};

const TableRow = ({ item, index, organizations, setOrganizations }: any) => {
  const navigate = useNavigate();

  const [showConfirmation, setConfirmation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleOpenConfirmation = () => {
    setConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setConfirmation(false);
  };

  const handleDeleteEvalaution = () => {
    setLoading(true);
    DeleteOrganizationService({ id: item?.id })
      .then(() => {
        toastMessage("success", "Organization deleted successfully");
        let tempOrgs = organizations?.filter(
          (org: any) => org?.id !== item?.id
        );
        setOrganizations(tempOrgs);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setLoading(false);
        setConfirmation(false);
      });
  };

  return (
    <>
      <tr>
        <td
          className={classNames(styles.tableContentLabel, "ps-4 pe-2 py-3")}
          style={{ width: "15%" }}
        >
          {moment(item?.created_at).format("MMM DD, YYYY")}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ width: "25%" }}
        >
          {item?.name}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ width: "15%" }}
        >
          {item?.users_count}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ width: "15%" }}
        >
          <div className={classNames("flex items-center justify-center")}>
            <div
              className={classNames(
                styles.statusContainer,
                !item?.disabled ? styles.activeStatus : styles.deActiveStatus
              )}
            >
              <label>{!item?.disabled ? "Active" : "Deactivated"}</label>
            </div>
          </div>
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{
            verticalAlign: "middle",
            textAlign: "center",
            width: "30%",
          }}
        >
          <div className={classNames("flex items-center gap-2 justify-center")}>
            <CustomToolTip label="See Details">
              <div
                className={classNames(styles.actionContainer)}
                onClick={() => {
                  navigate(
                    routeConstant.organizationDetail.path.replace(
                      ":id",
                      item?.id
                    )
                  );
                }}
              >
                <Icons.Eye />
              </div>
            </CustomToolTip>
            <CustomToolTip label="Edit">
              <div
                className={classNames(styles.actionContainer)}
                onClick={() => {
                  navigate(
                    routeConstant.editOrganization.path.replace(":id", item?.id)
                  );
                }}
              >
                <Icons.Pencil />
              </div>
            </CustomToolTip>
            <CustomToolTip label="Remove">
              <div
                className={classNames(styles.actionContainer)}
                onClick={() => {
                  handleOpenConfirmation();
                }}
              >
                <Icons.TrashOutline2 />
              </div>
            </CustomToolTip>
          </div>
        </td>
      </tr>
      <ConfirmationModal
        handleSubmit={handleDeleteEvalaution}
        show={showConfirmation}
        handleClose={handleCloseConfirmation}
        subtitle={`Are you sure you want to remove this organization?`}
        actionButtonText={"Remove"}
        loading={loading}
      />
    </>
  );
};
const TableRowLoader = () => {
  return (
    <>
      <tr>
        <td className={classNames(styles.tableContentLabel, "ps-4 pe-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.statusContainer)} />
          </div>
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center gap-2")}>
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
          </div>
        </td>
      </tr>
    </>
  );
};

export default Organizations;
